import { OBJECTIVE_NULL_BUCKET_LABEL } from './cockpit-data';

export const TACTIC_INTERVENTION_TYPES = [
  'create_campaign',
  'tg_assignment',
  'keyword_negation',
  'track_keyword',
  'objective_change',
  'reboot_campaign',
] as const;

export type TacticInterventionType = (typeof TACTIC_INTERVENTION_TYPES)[number];
export type TacticParamKind = 'text' | 'number' | 'enum';
export type TacticParamBinding = 'allocation' | 'default' | 'static';

export type TacticParamDefinition = {
  kind: TacticParamKind;
  label: string;
  options?: readonly string[];
  optionsFrom?: 'template_catalogue';
  min?: number;
  exclusiveMin?: boolean;
  step?: number;
};

export type TacticCatalogueEntry = {
  label: string;
  keys: readonly string[];
  channel_required: readonly string[];
  bindings: Readonly<Record<string, TacticParamBinding>>;
};

export const MATCH_TYPE_OPTIONS = [
  'EXACT',
  'PHRASE',
  'BROAD',
  'NEGATIVE_EXACT',
  'NEGATIVE_PHRASE',
] as const;

export type CampaignTemplateEntry = {
  name: string;
  campaign_type: string;
  funnel_stage: string;
  match_type: string;
  placement: string;
};

export const TEMPLATE_PLACEMENT_OPTIONS = ['ToS', 'RoS'] as const;
export const TEMPLATE_MATCH_TYPE_OPTIONS = ['EXACT', 'PHRASE', 'BROAD'] as const;
export const TEMPLATE_CAMPAIGN_TYPE_OPTIONS = ['SP'] as const;
export const TEMPLATE_BIDDING_STRATEGY_OPTIONS = ['LEGACY_FOR_SALES', 'AUTO_FOR_SALES', 'MANUAL'] as const;
export const TEMPLATE_BIDDING_STRATEGY_LABELS: Record<string, string> = {
  LEGACY_FOR_SALES: 'Dynamic bids - down only',
  AUTO_FOR_SALES: 'Dynamic bids - up and down',
  MANUAL: 'Fixed bids',
};
export const OBJECTIVE_OPTIONS = ['[EXPV14] AUTO-YIELD-RANK EXACT'] as const;
export const CAMPAIGN_STATE_OPTIONS = ['ENABLED'] as const;

export function siObjectiveOptions(
  objectives: string[],
  currentValue: string | null,
): { value: string; label: string; notInSi?: boolean }[] {
  const values = [...new Set(objectives.filter(
    (objective): objective is string => (
      typeof objective === 'string'
      && objective.trim() !== ''
      && objective !== OBJECTIVE_NULL_BUCKET_LABEL
    ),
  ))].sort((left, right) => left.localeCompare(right));
  // Annotated rather than inferred. Meteoric let `.map` infer `{value,label}[]`
  // and then pushed a third property onto it — which its looser tsconfig
  // tolerated and this repo's does not. The widened type is the one the
  // signature already promises, so the behaviour is unchanged and the hole the
  // stricter check found is closed rather than suppressed.
  const options: { value: string; label: string; notInSi?: boolean }[] = values.map((value) => ({
    value,
    label: value,
  }));
  if (currentValue && currentValue.trim() !== '' && !values.includes(currentValue)) {
    options.push({ value: currentValue, label: `${currentValue} (not in SI)`, notInSi: true });
  }
  return options;
}

export function templateOptionsFrom(catalogue: readonly CampaignTemplateEntry[]): {
  names: string[];
  campaign_type: string[];
  funnel_stage: string[];
  placement: string[];
} {
  return {
    names: catalogue.map((template) => template.name),
    campaign_type: [...new Set(catalogue.map((template) => template.campaign_type))],
    funnel_stage: [...new Set(catalogue.map((template) => template.funnel_stage))],
    placement: [...new Set(catalogue.map((template) => template.placement))],
  };
}

export function campaignTemplateByName(
  name: unknown,
  catalogue: readonly CampaignTemplateEntry[],
): CampaignTemplateEntry | null {
  if (typeof name !== 'string' || !name) return null;
  return catalogue.find((template) => template.name === name) ?? null;
}

/** The three static params a template name determines. */
export function campaignTemplateDerivedParams(
  name: unknown,
  catalogue: readonly CampaignTemplateEntry[],
): Pick<CampaignTemplateEntry, 'campaign_type' | 'funnel_stage' | 'placement'> | null {
  const template = campaignTemplateByName(name, catalogue);
  if (!template) return null;
  return {
    campaign_type: template.campaign_type,
    funnel_stage: template.funnel_stage,
    placement: template.placement,
  };
}

export const TACTIC_PARAM_DEFINITIONS: Readonly<Record<string, TacticParamDefinition>> = {
  keyword: { kind: 'text', label: 'Keyword' },
  market: { kind: 'text', label: 'Market' },
  asin: { kind: 'text', label: 'ASIN' },
  target_group: { kind: 'text', label: 'Target group' },
  targeting: { kind: 'text', label: 'Targeting' },
  campaign_id: { kind: 'text', label: 'Campaign ID' },
  target_ref: { kind: 'text', label: 'Target reference' },
  budget: { kind: 'number', label: 'Budget', min: 0, exclusiveMin: true, step: 0.01 },
  bid: { kind: 'number', label: 'Bid', min: 0, exclusiveMin: true, step: 0.01 },
  match_type: { kind: 'enum', label: 'Match type', options: MATCH_TYPE_OPTIONS },
  campaign_type: { kind: 'enum', label: 'Campaign type', optionsFrom: 'template_catalogue' },
  funnel_stage: { kind: 'enum', label: 'Funnel stage', optionsFrom: 'template_catalogue' },
  placement: { kind: 'enum', label: 'Placement', optionsFrom: 'template_catalogue' },
  template: { kind: 'enum', label: 'Campaign template', optionsFrom: 'template_catalogue' },
  objective: { kind: 'enum', label: 'Objective', options: OBJECTIVE_OPTIONS },
  state: { kind: 'enum', label: 'State', options: CAMPAIGN_STATE_OPTIONS },
};

export const TACTIC_CATALOGUE: Readonly<Record<TacticInterventionType, TacticCatalogueEntry>> = {
  create_campaign: {
    label: 'Create campaign',
    keys: ['keyword', 'market', 'asin', 'target_group', 'targeting', 'budget', 'bid', 'campaign_type', 'funnel_stage', 'template', 'placement'],
    channel_required: ['keyword', 'market', 'asin', 'target_group', 'targeting', 'budget', 'bid'],
    bindings: {
      keyword: 'allocation',
      market: 'allocation',
      asin: 'allocation',
      target_group: 'allocation',
      targeting: 'allocation',
      budget: 'default',
      bid: 'default',
      campaign_type: 'static',
      funnel_stage: 'static',
      template: 'static',
      placement: 'static',
    },
  },
  tg_assignment: {
    label: 'Target group assignment',
    keys: ['keyword', 'market', 'asin', 'target_group'],
    channel_required: ['keyword', 'market', 'asin', 'target_group'],
    bindings: {
      keyword: 'allocation',
      market: 'allocation',
      asin: 'allocation',
      target_group: 'allocation',
    },
  },
  keyword_negation: {
    label: 'Keyword negation',
    keys: ['keyword', 'market', 'campaign_id', 'match_type'],
    channel_required: ['keyword', 'market', 'campaign_id', 'match_type'],
    bindings: {
      keyword: 'allocation',
      market: 'allocation',
      campaign_id: 'allocation',
      match_type: 'default',
    },
  },
  track_keyword: {
    label: 'Track keyword',
    keys: ['keyword', 'market', 'asin'],
    channel_required: [],
    bindings: {
      keyword: 'allocation',
      market: 'allocation',
      asin: 'allocation',
    },
  },
  objective_change: {
    label: 'Change objective',
    keys: ['keyword', 'market', 'target_ref', 'objective'],
    channel_required: [],
    bindings: {
      keyword: 'allocation',
      market: 'allocation',
      target_ref: 'allocation',
      objective: 'default',
    },
  },
  reboot_campaign: {
    label: 'Reboot campaign',
    keys: ['keyword', 'market', 'target_ref', 'state'],
    channel_required: ['keyword', 'market', 'target_ref', 'state'],
    bindings: {
      keyword: 'allocation',
      market: 'allocation',
      target_ref: 'allocation',
      state: 'default',
    },
  },
};

export const TACTIC_SOURCE_PREFIXES = ['item.', 'payload.', 'context.', 'economics.', 'config.'] as const;

export function isTacticSourcePath(value: unknown): boolean {
  return typeof value === 'string' && TACTIC_SOURCE_PREFIXES.some((prefix) => value.startsWith(prefix));
}

export const TACTIC_PARAM_TEMPLATE_DEFAULTS: Readonly<Record<TacticInterventionType, Readonly<Record<string, string>>>> = {
  create_campaign: {
    keyword: 'item.keyword',
    market: 'item.market',
    asin: 'item.asin',
    target_group: 'context.target_group',
    targeting: 'item.keyword',
    budget: 'config.campaign_default_budget',
    bid: 'config.market_default_bid',
    campaign_type: 'SP',
    funnel_stage: 'CONVERSION',
    template: '',
    placement: 'ToS',
  },
  tg_assignment: {
    keyword: 'item.keyword',
    market: 'item.market',
    asin: 'item.asin',
    target_group: 'payload.target_group',
  },
  keyword_negation: {
    keyword: 'item.keyword',
    market: 'item.market',
    campaign_id: 'context.campaign_id',
    match_type: 'config.negative_match_type',
  },
  track_keyword: {
    keyword: 'item.keyword',
    market: 'item.market',
    asin: 'item.asin',
  },
  objective_change: {
    keyword: 'item.keyword',
    market: 'item.market',
    target_ref: 'context.campaign_id',
    objective: 'config.default_objective',
  },
  reboot_campaign: {
    keyword: 'item.keyword',
    market: 'item.market',
    target_ref: 'context.campaign_id',
    state: 'config.enabled_state',
  },
};

export function isTacticInterventionType(value: string): value is TacticInterventionType {
  return TACTIC_INTERVENTION_TYPES.includes(value as TacticInterventionType);
}

export function validateTacticParams(
  interventionType: string,
  params: Record<string, unknown>,
  catalogue?: readonly CampaignTemplateEntry[],
): Record<string, string> {
  if (!isTacticInterventionType(interventionType)) {
    return { intervention_type: 'Choose a supported intervention type.' };
  }
  const errors: Record<string, string> = {};
  if (interventionType === 'create_campaign') {
    const templateName = typeof params.template === 'string' ? params.template.trim() : '';
    const template = catalogue ? campaignTemplateByName(templateName, catalogue) : null;
    if (!templateName || (catalogue && !template)) {
      errors.template = 'Campaign template must be one of the configured templates.';
    } else if (template) {
      for (const key of ['campaign_type', 'funnel_stage', 'placement'] as const) {
        if (params[key] !== template[key]) {
          const label = TACTIC_PARAM_DEFINITIONS[key].label;
          errors[key] = `${label} must be ${template[key]} for template ${template.name}.`;
        }
      }
    }
  }
  for (const key of TACTIC_CATALOGUE[interventionType].keys) {
    if (
      interventionType === 'create_campaign' &&
      (key === 'template' || key === 'campaign_type' || key === 'funnel_stage' || key === 'placement')
    ) {
      continue;
    }
    const definition = TACTIC_PARAM_DEFINITIONS[key];
    const binding = TACTIC_CATALOGUE[interventionType].bindings[key];
    const value = params[key];
    const blank = value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    if (binding === 'allocation' || (binding === 'default' && (blank || isTacticSourcePath(value)))) {
      continue;
    }
    if (blank) {
      errors[key] = `${definition.label} is required.`;
      continue;
    }
    if (binding === 'static' && isTacticSourcePath(value)) {
      errors[key] = `${definition.label} must be a fixed value.`;
      continue;
    }
    if (definition.kind === 'number') {
      const numeric = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(numeric) || (definition.exclusiveMin ? numeric <= (definition.min ?? 0) : numeric < (definition.min ?? 0))) {
        errors[key] = `${definition.label} must be greater than ${definition.min ?? 0}.`;
      }
    }
    if (definition.kind === 'enum' && definition.options && !definition.options.includes(String(value))) {
      errors[key] = `Choose a valid ${definition.label.toLowerCase()}.`;
    }
  }
  return errors;
}

export function buildTacticPayloadParams(
  interventionType: TacticInterventionType,
  params: Record<string, unknown>,
  catalogue?: readonly CampaignTemplateEntry[],
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const entry = TACTIC_CATALOGUE[interventionType];
  const requestedTemplateName = typeof params.template === 'string' ? params.template.trim() : '';
  const campaignTemplateName = interventionType === 'create_campaign' && catalogue
    ? (campaignTemplateByName(requestedTemplateName, catalogue)?.name ?? catalogue[0]?.name ?? '')
    : requestedTemplateName;
  const derivedCampaignParams = interventionType === 'create_campaign' && catalogue
    ? campaignTemplateDerivedParams(campaignTemplateName, catalogue)
    : null;
  for (const key of entry.keys) {
    const binding = entry.bindings[key];
    const definition = TACTIC_PARAM_DEFINITIONS[key];
    const value = params[key];
    const blank = value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    if (binding === 'allocation') {
      payload[key] = blank ? TACTIC_PARAM_TEMPLATE_DEFAULTS[interventionType][key] : value;
    } else if (binding === 'default') {
      if (blank || isTacticSourcePath(value)) {
        payload[key] = blank ? TACTIC_PARAM_TEMPLATE_DEFAULTS[interventionType][key] : value;
      } else {
        payload[key] = definition.kind === 'number' ? Number(value) : String(value);
      }
    } else if (interventionType === 'create_campaign' && key === 'template') {
      payload[key] = campaignTemplateName;
    } else if (
      interventionType === 'create_campaign' &&
      (key === 'campaign_type' || key === 'funnel_stage' || key === 'placement')
    ) {
      payload[key] = derivedCampaignParams?.[key] ?? TACTIC_PARAM_TEMPLATE_DEFAULTS.create_campaign[key];
    } else {
      payload[key] = String(value ?? '');
    }
  }
  return payload;
}
